// SearchSpotlight — Synapse's universal search, on the kit's <Spotlight> surface.
// (Formerly TopBar.jsx — the visible top bar is long gone; the component had
// become ONLY this modal, so the name now tells the truth.)
//
// The kit owns the surface (backdrop, glass panel, input row, sections, items,
// empty states); Synapse owns what's genuinely Synapse: the filter chip, the
// Tab-to-scope hint, channel/people/message search and their result rows.
import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import {
  avatarColorIndex,
  Spotlight, SpotlightInput, SpotlightClear, SpotlightResults,
  SpotlightSection, SpotlightItem, SpotlightEmpty,
} from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import { searchMessages, searchMessagesGlobal, searchUsers } from '../services/api'
import { formatTime } from '../utils/time'

const RECENT_KEY = 'synapse_recent_searches'

function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || []
  } catch { return [] }
}

function saveRecent(list) {
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)))
}

function getFilterLabel(channel) {
  if (!channel) return null
  if (channel.is_dm) return `@${channel.other_username || 'DM'}`
  return `#${channel.name}`
}

const ClockGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

export default forwardRef(function SearchSpotlight(props, ref) {
  const { currentUser, channels, dmChannels, members, activeChannel, selectChannel, createDM, navigateToMessage, openThreadById } = useChat()

  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState(null) // { channelId, label } or null
  const [msgResults, setMsgResults] = useState([])
  const [peopleResults, setPeopleResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [recentSearches, setRecentSearches] = useState(loadRecent)

  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Expose open / toggle / openWithFilter to parent via ref
  useImperativeHandle(ref, () => ({
    open() {
      handleOpen()
    },
    // The rail's Search seat toggles: a second click on the trigger dismisses (the rail
    // chevron's "click again to collapse"). No deps on this handle, so isOpen is always fresh.
    toggle() {
      if (isOpen) setIsOpen(false)
      else handleOpen()
    },
    openWithFilter(channel) {
      const label = getFilterLabel(channel)
      setFilter(label ? { channelId: channel.id, label: `in:${label}` } : null)
      setQuery('')
      setMsgResults([])
      setRecentSearches(loadRecent())
      setIsOpen(true)
    },
    // The command dock's fallthrough seam: an unparsed dock line is a SEARCH,
    // carried over as the live query (the debounced search fires on open).
    openWithQuery(q) {
      setFilter(null)
      setQuery(q || '')
      setMsgResults([])
      setRecentSearches(loadRecent())
      setIsOpen(true)
    }
  }))

  // THE SCOPE IS THE WHOLE APP unless you narrow it. A chip means "in this
  // conversation" and runs the channel-scoped search; without one the query
  // goes to every channel you are in. (It used to fall back to the ACTIVE
  // channel with nothing on screen saying so, which is why a search that
  // should have found a message anywhere quietly found nothing.)
  const searchChannelId = filter ? filter.channelId : null

  // Debounced search — messages and people go out together, because a name
  // typed into this box is as likely to mean "find them" as "find what they
  // said", and the surface should not make you choose first.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = query.trim()

    if (q.length < 2) {
      setMsgResults([])
      setPeopleResults([])
      setSearching(false)
      return
    }

    setSearching(true)
    debounceRef.current = setTimeout(async () => {
      const [msgs, people] = await Promise.all([
        (searchChannelId ? searchMessages(searchChannelId, q) : searchMessagesGlobal(q))
          .then(r => r.messages || [])
          .catch(err => { console.error('Message search failed:', err); return [] }),
        // The directory only matters when the search is app-wide: inside a
        // chip you are asking about a conversation, not about who exists.
        // A failure here is not a failed search — the local list still
        // answers below — so it resolves empty rather than throwing.
        searchChannelId ? Promise.resolve([]) : searchUsers(q).then(r => r.users || []).catch(() => []),
      ])
      setMsgResults(msgs)
      setPeopleResults(people)
      setSearching(false)
    }, 300)

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, searchChannelId])

  // Filter channels + DMs (only when no filter chip is active)
  const filteredChannels = query.trim() && !filter
    ? [...(channels || []), ...(dmChannels || [])].filter(ch => {
        const label = ch.is_dm ? (ch.other_username || '') : (ch.name || '')
        return label.toLowerCase().includes(query.toLowerCase())
      }).slice(0, 5)
    : []

  // People: the members already in hand answer INSTANTLY (no round trip for
  // someone you share a channel with), and the directory's results fold in
  // behind them, deduped by id. Two sources, one list, nearest first.
  const filteredPeople = query.trim() && !filter
    ? (() => {
        const q = query.toLowerCase()
        const out = []
        const seen = new Set([currentUser?.id])
        // Both sources say user_id; only the local list was ever read as `id`,
        // which is why every person row keyed on undefined and clicking one
        // opened a DM with [undefined]. Normalise once, here, and the rows
        // below take one shape whatever answered.
        for (const m of [...(members || []), ...peopleResults]) {
          const id = m.user_id || m.id
          if (!id || seen.has(id) || !m.username?.toLowerCase().includes(q)) continue
          seen.add(id)
          out.push({ id, username: m.username })
        }
        return out.slice(0, 5)
      })()
    : []

  const hasResults = filteredChannels.length > 0 || filteredPeople.length > 0 || msgResults.length > 0

  // Where a hit LIVES. Global search means a message row without its channel
  // is an answer you can't place, so every row carries one.
  function channelLabelFor(id) {
    const ch = [...(channels || []), ...(dmChannels || [])].find(c => c.id === id)
    if (!ch) return null
    return ch.is_dm ? `@${ch.other_username || 'DM'}` : `#${ch.name}`
  }

  // A hit with a thread_id is a REPLY, and its home is the thread, not the
  // stream — jumping to the channel would land you nowhere near it.
  function handleMessageClick(msg) {
    commitSearch(query)
    const channelId = msg.channelId || searchChannelId
    if (msg.thread_id) openThreadById(channelId, msg.thread_id, msg.id)
    else navigateToMessage(channelId, msg.id)
    setIsOpen(false)
  }

  function commitSearch(q) {
    if (!q.trim()) return
    const updated = [q.trim(), ...recentSearches.filter(s => s !== q.trim())].slice(0, 5)
    setRecentSearches(updated)
    saveRecent(updated)
  }

  function handleOpen() {
    setIsOpen(true)
    setQuery('')
    setFilter(null)
    setMsgResults([])
    setRecentSearches(loadRecent())
  }

  function handleChannelClick(ch) {
    commitSearch(query)
    selectChannel(ch)
    setIsOpen(false)
  }

  function handlePersonClick(person) {
    commitSearch(query)
    createDM([person.id], { [person.id]: person.username })
    setIsOpen(false)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && query.trim()) {
      commitSearch(query)
    }
    // Tab to scope search to active channel
    if (e.key === 'Tab' && !filter && activeChannel) {
      e.preventDefault()
      const label = getFilterLabel(activeChannel)
      setFilter({ channelId: activeChannel.id, label: `in:${label}` })
    }
    // Backspace on empty query removes the filter chip
    if (e.key === 'Backspace' && !query && filter) {
      setFilter(null)
      setMsgResults([])
    }
  }

  function clearRecent() {
    setRecentSearches([])
    saveRecent([])
  }

  function removeFilter() {
    setFilter(null)
    setMsgResults([])
    inputRef.current?.focus()
  }

  function channelIcon(ch) {
    if (ch.is_dm) {
      const dmName = ch.other_username || 'DM'
      const ci = avatarColorIndex(dmName)
      return <span className="tbs-avatar-sm" style={{ background: `var(--avatar-${ci})` }}>{dmName[0]?.toUpperCase()}</span>
    }
    if (ch.type === 'private') {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      )
    }
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="4" y1="9" x2="20" y2="9" />
        <line x1="4" y1="15" x2="20" y2="15" />
        <line x1="10" y1="3" x2="8" y2="21" />
        <line x1="16" y1="3" x2="14" y2="21" />
      </svg>
    )
  }

  const filterScopeLabel = filter
    ? filter.label.replace('in:', '')
    : activeChannel
      ? (activeChannel.is_dm ? `@${activeChannel.other_username || 'DM'}` : `#${activeChannel.name}`)
      : null

  // Stay MOUNTED and drive the surface with `open` (the endgame Radix pattern) — so the Dialog owns the
  // full lifecycle: focus trap on open, focus-RETURN to the opener on close, scroll-lock/unlock, Escape.
  // When closed, query is '' (handleOpen resets it), so the derived result lists above are all empty —
  // no search work happens while hidden, and Radix unmounts the panel content itself.
  return (
    <Spotlight open={isOpen} onClose={() => setIsOpen(false)} label="Search">
      <SpotlightInput
        inputRef={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={filter ? `Search messages ${filter.label.replace('in:', 'in ')}...` : 'Search messages, people, channels and threads'}
        before={filter && (
          <span className="tbs-filter-chip" onClick={removeFilter}>
            {filter.label}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        )}
        after={(
          <>
            {!filter && activeChannel && !query && (
              <span className="tbs-tab-hint">
                <kbd>Tab</kbd> for #{activeChannel.is_dm ? (activeChannel.other_username || 'DM') : activeChannel.name}
              </span>
            )}
            {(query || filter) && (
              <SpotlightClear onClick={() => { setQuery(''); setFilter(null); setMsgResults([]) }} />
            )}
          </>
        )}
      />

      <SpotlightResults>
        {/* Empty state: recent searches + quick channel search */}
        {!query.trim() && !filter && (
          <>
            {recentSearches.length > 0 && (
              <SpotlightSection title="Recent" action={<button onClick={clearRecent}>Clear</button>}>
                {recentSearches.map((q, i) => (
                  <SpotlightItem key={i} icon={<ClockGlyph />} onClick={() => setQuery(q)}>{q}</SpotlightItem>
                ))}
              </SpotlightSection>
            )}
          </>
        )}

        {/* Filter active but no query yet -- prompt to type */}
        {!query.trim() && filter && (
          <SpotlightEmpty>Type to search messages {filter.label.replace('in:', 'in ')}</SpotlightEmpty>
        )}

        {/* Results when query is present */}
        {query.trim() && (
          <>
            {filteredChannels.length > 0 && (
              <SpotlightSection title="Channels">
                {filteredChannels.map(ch => (
                  <SpotlightItem key={ch.id} icon={channelIcon(ch)} onClick={() => handleChannelClick(ch)}>
                    {ch.is_dm ? (ch.other_username || 'DM') : ch.name}
                    {ch.group_name && <span className="tbs-item-group"> · {ch.group_name}</span>}
                  </SpotlightItem>
                ))}
              </SpotlightSection>
            )}

            {filteredPeople.length > 0 && (
              <SpotlightSection title="People">
                {filteredPeople.map(p => {
                  const pi = avatarColorIndex(p.username)
                  return (
                    <SpotlightItem
                      key={p.id}
                      icon={<span className="tbs-avatar-sm" style={{ background: `var(--avatar-${pi})` }}>{p.username[0]?.toUpperCase()}</span>}
                      onClick={() => handlePersonClick(p)}
                    >
                      {p.username}
                    </SpotlightItem>
                  )
                })}
              </SpotlightSection>
            )}

            {msgResults.length > 0 && (
              <SpotlightSection title={filter ? `Messages in ${filterScopeLabel}` : 'Messages'}>
                {msgResults.slice(0, 8).map(msg => {
                  /* Stacked internals are this app's, so the row composes the
                     kit's classes by hand (the supported escape hatch) — BOTH
                     of them: .ofself-flush-row is where the row actually
                     lives (full-bleed, seam, hover, hit area) and
                     .ofself-spotlight__item only tunes it for search. With
                     the item class alone these were bare <button>s, boxed and
                     sitting side by side. */
                  const where = !filter && channelLabelFor(msg.channelId)
                  return (
                    <button key={msg.id} className="ofself-flush-row ofself-spotlight__item tbs-msg-item" onClick={() => handleMessageClick(msg)}>
                      <div className="tbs-msg-top">
                        <span className="tbs-msg-sender">{msg.sender?.username || 'Unknown'}</span>
                        {where && <span className="tbs-item-group">{where}</span>}
                        {msg.thread_id && <span className="tbs-item-group">in thread</span>}
                        <span className="tbs-msg-time">{msg.timestamp ? formatTime(msg.timestamp) : ''}</span>
                      </div>
                      <span className="tbs-msg-preview">{msg.content?.slice(0, 80)}{msg.content?.length > 80 ? '...' : ''}</span>
                    </button>
                  )
                })}
              </SpotlightSection>
            )}

            {searching && !hasResults && <SpotlightEmpty>Searching…</SpotlightEmpty>}

            {!searching && query.trim().length >= 2 && !hasResults && (
              <SpotlightEmpty>No results for "{query}"</SpotlightEmpty>
            )}
          </>
        )}
      </SpotlightResults>
    </Spotlight>
  )
})
